import React, { useEffect, useState } from "react";
import { useMemo } from "react";
import {
  sizeMap,
  colorMap,
  materialMap,
  AlbumOrder,
} from "../Utilities/mapUtil";
import { useDispatch, useSelector } from "react-redux";
import { modeActions } from "../store/modeSlice";
import appConst from "../Utilities/appConst.json";

export const useEventGA4Map = () => {
  const dispatch = useDispatch();
  const product = sessionStorage.getItem(appConst.PRODUCT);
  const orderID = useSelector((state) => state.basket.orderID);
  const userEmail = useSelector((state) => state.basket.user_email) || "";
  const basketItems = useSelector((state) => state.basket.basketItems);
  const basket = useSelector((state) => state.basket.basket);
  const shippingMethod = useSelector(
    (state) => state.shipping.selectedShippingMethod
  );
  const selectableCoupons = useSelector((state) => state.coupons.coupons);
  const selectable = useSelector((state) => state.coupons.combinations);
  const valueGA4 = useSelector((state) => state.mode.valueGA4);
  const isCheckedBusinessInvoice = useSelector(
    (state) => state.personalDetails.isCheckedBusinessInvoice
  );
  const isCheckedOtherReceiver = useSelector(
    (state) => state.personalDetails.isCheckedOtherReceiver
  );

  const getItemName = (item) => {
    let itemName = "";
    switch (product) {
      case appConst.TILES:
        itemName = item.format
          .toString()
          .startsWith(appConst.MOSAIC_FORMAT_PREFIX)
          ? "mosaic"
          : "tiles";
        break;
      case appConst.CALENDARS:
        itemName = "calendars";
        break;
    }
    return itemName;
  };

  const gaItems = basketItems.map((item) => ({
    // Construct the ,gaItems array
    item_name: getItemName(item),
    item_id: item.item_id,
    item_variant: colorMap.get(item.frame_color),
    materialMap: materialMap.get(item.material),
    price: item.quantity * item.unit_price,
    quantity: item.quantity,
    size: item.format.toString().startsWith(appConst.MOSAIC_FORMAT_PREFIX)
      ? `${item.format.toString()[1]} X ${item.format.toString()[2]}`
      : sizeMap.get(item.format),
  }));

  const shippingText = {
    shipping_name:
      shippingMethod && shippingMethod.length > 0 && shippingMethod.displayName
        ? shippingMethod.displayName.replace(/[^\u0590-\u05FF\s]/g, "")
        : null,
    shipping_price:
      shippingMethod && shippingMethod.length > 0 ? shippingMethod.price : null,
  };
  const shipping = shippingText.shipping_name
    ? `${shippingText.shipping_name} - ${shippingText.shipping_price}`
    : "";

  const basketAmount = basket
    ? basket.payments
      ? basket.payments.amount_due
      : ""
    : "";

  const selectedItems = selectable.filter((item) => item.is_selected);

  const coupons = selectableCoupons
    .filter((item1) => selectedItems.some((item2) => item1.id === item2.id))
    .map((item) => item.coupon_name);

  useEffect(() => {
    if (valueGA4.length > 0) {
      valueGA4.forEach((element) => {
        const value = eventGA4Map.get(element.value);
        if (!value) {
          // No GA4 mapping for this queued value — e.g. an external redirect
          dispatch(modeActions.popValueGA4());
          return;
        }
        value.event_context = element.context || "";
        dispatch(modeActions.popValueGA4());
        if (value.event != appConst.PAGE_VIEW) {
          window.dataLayer.push(null);
        } else {
          value.ecommerce = null;
        }
        window.dataLayer.push(value);
      });
    }
  }, [valueGA4]);

  // Create the AlbumOrder object and map it to the 'to the shopping cart' event
  const eventGA4Map = useMemo(() => {
    const map = new Map();
    if (gaItems.length) {
      map.set(
        appConst.to_the_shopping_cart,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.begin_checkout,
              appConst.begin_checkout,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              "",
              "",
              "",
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.number_of_copies,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.choose_quantity,
              appConst.choose_quantity,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              "",
              "",
              "",
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.delivery_details,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.add_shipping_info,
              appConst.add_shipping_info,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              "",
              "",
              "",
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.add_personal_details,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.add_personal_details,
              appConst.add_personal_details,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              "",
              "",
              "",
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.confirm_order,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.purchase,
              appConst.purchase,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              orderID,
              (basketAmount * 0.17).toFixed(2),
              (basketAmount * 0.83).toFixed(2),
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.insert_coupon,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.insert_coupon,
              appConst.insert_coupon,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              "",
              "",
              "",
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.order_now_button,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.continue_checkout,
              appConst.continue_checkout,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              "",
              "",
              "",
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.shipping_phone,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.add_shipping_phone,
              appConst.add_shipping_phone,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              "",
              "",
              "",
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
      map.set(
        appConst.purchase_notes_when_purchase_is_not_fully_complete,
        JSON.parse(
          JSON.stringify(
            new AlbumOrder(
              appConst.show_purchase_note,
              appConst.show_purchase_note,
              null,
              userEmail,
              "",
              product,
              product,
              orderID,
              isCheckedBusinessInvoice,
              isCheckedOtherReceiver,
              orderID,
              (basketAmount * 0.17).toFixed(2),
              (basketAmount * 0.83).toFixed(2),
              shipping,
              coupons.join(","),
              appConst.currency,
              gaItems
            )
          )
        )
      );
    }
    map.set(
      appConst.FIRST_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.FIRST_PAGE))
      )
    );
    map.set(
      appConst.SECOND_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.SECOND_PAGE))
      )
    );
    map.set(
      appConst.FIFTH_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.FIFTH_PAGE))
      )
    );
    map.set(
      appConst.SIXTH_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.SIXTH_PAGE))
      )
    );
    map.set(
      appConst.SEVENTH_PAGE,
      JSON.parse(
        JSON.stringify(
          new AlbumOrder(appConst.PAGE_VIEW, appConst.SEVENTH_PAGE)
        )
      )
    );
    map.set(
      appConst.EIGHTH_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.EIGHTH_PAGE))
      )
    );
    map.set(
      appConst.NINTH_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.NINTH_PAGE))
      )
    );
    map.set(
      appConst.TENTH_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.TENTH_PAGE))
      )
    );
    map.set(
      appConst.ELEVNTH_PAGE,
      JSON.parse(
        JSON.stringify(
          new AlbumOrder(appConst.PAGE_VIEW, appConst.ELEVNTH_PAGE)
        )
      )
    );
    map.set(
      appConst.TWELTH_PAGE,
      JSON.parse(
        JSON.stringify(new AlbumOrder(appConst.PAGE_VIEW, appConst.TWELTH_PAGE))
      )
    );

    return map;
  }, [gaItems]);

  return eventGA4Map;
};
